import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { useBrand } from '../BrandContext';
import { useAuth } from '../AuthContext';

import KpiStrip from '../components/UnitsDeals/KpiStrip';
import UnitsBrowser from '../components/UnitsDeals/UnitsBrowser';
import DealsTracker from '../components/UnitsDeals/DealsTracker';
import DealRegisterModal from '../components/UnitsDeals/DealRegisterModal';
import DealEditModal from '../components/UnitsDeals/DealEditModal';
import UnitEditModal from '../components/UnitsDeals/UnitEditModal';
import UnitCreateModal from '../components/UnitsDeals/UnitCreateModal';
import ClosureDashboard from '../components/UnitsDeals/ClosureDashboard';
import ReportsTab from '../components/UnitsDeals/ReportsTab';
import SettingsTab from '../components/UnitsDeals/SettingsTab';
import LoadError from '../components/LoadError';

// ── Units & Deals page ──
// 4 tabs: Units (inventory) · Deals (tracking) · Reports · Settings.
// Mobile-responsive throughout (tables → stacked rows under 768px).
export default function UnitsDeals() {
  const { t, lang } = useLanguage();
  const { can } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const sub = location.pathname.replace('/units-deals', '').replace(/^\//, '');
  const tab = sub === 'deals' ? 'deals' : sub === 'closure' ? 'closure' : sub === 'reports' ? 'reports' : sub === 'settings' ? 'settings' : 'units';
  const [units, setUnits] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({ total: 0, byStatus: {} });
  const [brokers, setBrokers] = useState([]);
  const [team, setTeam] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [dealUnit, setDealUnit] = useState(null);
  const [dealPickerOpen, setDealPickerOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [createUnitOpen, setCreateUnitOpen] = useState(false);

  const loadData = useCallback(async () => {
    setError(false);
    try {
      const [unitsRes, dealsRes, statsRes, brokersRes, teamRes, cfgRes] = await Promise.all([
        axios.get('/api/units-deals/units'),
        axios.get('/api/units-deals/deals'),
        axios.get('/api/units-deals/units/stats'),
        axios.get('/api/units-deals/brokers'),
        axios.get('/api/units-deals/team'),
        axios.get('/api/units-deals/config'),
      ]);
      setUnits(unitsRes.data.units || []);
      setDeals(dealsRes.data.deals || []);
      setStats(statsRes.data || { total: 0, byStatus: {} });
      setBrokers(brokersRes.data.brokers || []);
      setTeam(teamRes.data.team || []);
      setConfig(cfgRes.data || {});
    } catch (err) {
      // A 401 is handled globally (logout); anything else (network / 5xx during a
      // deploy restart) surfaces a retry state so an empty screen never reads as
      // "the deal disappeared".
      console.error('UnitsDeals load error:', err);
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveUnit = async (id, form) => {
    await axios.put(`/api/units-deals/units/${id}`, form);
    setEditUnit(null);
    loadData();
  };

  // One-click "Mark as Sold" from the Closure Dashboard. Completing the closure
  // checklist doesn't move the unit off محجوز on its own, so this flips a fully
  // closed deal to مباع. Re-sends the unit's current fields with status overridden
  // (مباع triggers no reservation-sync in PUT /units), preserving everything else.
  const handleMarkSold = async (unit) => {
    const msg = lang === 'ar'
      ? `تأكيد بيع الوحدة ${unit.unit_code}؟ ستظهر كـ «مباع» بدل «محجوز».`
      : `Mark unit ${unit.unit_code} as Sold? It will show as «مباع» instead of «محجوز».`;
    if (!window.confirm(msg)) return;
    const raw = units.find((x) => x.id === unit.id) || unit;
    await axios.put(`/api/units-deals/units/${unit.id}`, { ...raw, status: 'مباع' });
    loadData();
  };

  const handleRegisterDeal = async (deal) => {
    await axios.post('/api/units-deals/deals', deal);
    setDealUnit(null);
    setDealPickerOpen(false);
    loadData();
  };

  const launchDealPicker = () => {
    setDealUnit(null);
    setDealPickerOpen(true);
  };

  const handleUpdateDeal = async (id, deal) => {
    await axios.put(`/api/units-deals/deals/${id}`, deal);
    setEditingDeal(null);
    loadData();
  };

  const handleDeleteDeal = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'حذف الصفقة؟ سيتم إرجاع الوحدة إلى متاحة.' : 'Delete deal? The unit will revert to Available.')) return;
    await axios.delete(`/api/units-deals/deals/${id}`);
    setEditingDeal(null); // close the edit modal if the delete came from there
    loadData();
  };

  // Create a unit inline (used when a tenant starts with empty inventory or
  // needs to add a unit that wasn't part of the seeded master list).
  const handleCreateUnit = async (unit) => {
    const res = await axios.post('/api/units-deals/units', unit);
    setCreateUnitOpen(false);
    loadData();
    return res.data;
  };

  const handleAddBroker = async (broker) => {
    await axios.post('/api/units-deals/brokers', broker);
    loadData();
  };

  const handleDeleteBroker = async (id) => {
    await axios.delete(`/api/units-deals/brokers/${id}`);
    loadData();
  };

  const handleSaveConfig = async (cfg) => {
    const res = await axios.put('/api/units-deals/config', cfg);
    // Persist the cleaned config (without the _applyToHistory directive) so
    // subsequent edits diff correctly against the saved baseline.
    const { _applyToHistory, ...clean } = cfg;
    setConfig(clean);
    // If the server updated historical deal rows, refresh deals so the UI
    // immediately reflects the recomputed commissions.
    if (res.data && res.data.updatedCount > 0) {
      loadData();
    }
    return res.data;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.4 }}>&#9203;</div>
        <div style={{ color: 'var(--text-secondary)' }}>{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return <LoadError onRetry={loadData} />;
  }

  return (
    <div>
      {tab === 'units' && (
        <>
          <KpiStrip stats={stats} deals={deals} lang={lang} />
          <UnitsBrowser
            units={units}
            brandProjects={brand.projects || []}
            onRegisterDeal={can('deals.create') ? (u) => setDealUnit(u) : undefined}
            onLaunchDeal={can('deals.create') ? launchDealPicker : undefined}
            onAddUnit={can('units.create') ? () => setCreateUnitOpen(true) : undefined}
            onEdit={!can('units.edit') ? undefined : (u) => {
              // Reserved/sold/transferred units always have a linked deal — route
              // them to the unified DealEditModal so the user edits both the deal
              // commission AND the unit pipeline in one place.
              const hasDeal = u.status === 'محجوز' || u.status === 'مباع' || u.status === 'أفرغ';
              if (hasDeal) {
                const d = deals.find((x) => x.project === u.project && x.unit_code === u.unit_code);
                if (d) { setEditingDeal({ ...d, _unit: u }); return; }
              }
              setEditUnit(u);
            }}
            t={t}
            lang={lang}
          />
        </>
      )}

      {tab === 'deals' && (
        <DealsTracker
          deals={deals}
          units={units}
          team={team}
          onDelete={can('deals.delete') ? handleDeleteDeal : undefined}
          onEdit={!can('deals.edit') ? undefined : (d) => {
            // Attach the linked unit so DealEditModal can pre-fill pipeline stage
            // and transfer date (these live on the unit row, not the deal row).
            const u = units.find((x) => x.project === d.project && x.unit_code === d.unit_code);
            setEditingDeal({ ...d, _unit: u || null });
          }}
          onLaunchDeal={can('deals.create') ? launchDealPicker : undefined}
          t={t}
          lang={lang}
        />
      )}

      {tab === 'closure' && (
        <ClosureDashboard
          units={units}
          deals={deals}
          lang={lang}
          t={t}
          onEditDeal={(d, u) => setEditingDeal({ ...d, _unit: u || null })}
          onMarkSold={can('units.edit') ? handleMarkSold : undefined}
        />
      )}

      {tab === 'reports' && <ReportsTab t={t} lang={lang} />}

      {tab === 'settings' && (
        <SettingsTab
          team={team}
          brokers={brokers}
          deals={deals}
          config={config}
          onAddBroker={handleAddBroker}
          onDeleteBroker={handleDeleteBroker}
          onSaveConfig={handleSaveConfig}
          t={t}
          lang={lang}
        />
      )}

      {(dealUnit || dealPickerOpen) && (
        <DealRegisterModal
          unit={dealUnit}
          units={units}
          brandProjects={brand.projects || []}
          brokers={brokers}
          team={team}
          onClose={() => { setDealUnit(null); setDealPickerOpen(false); }}
          onSubmit={handleRegisterDeal}
          onAddBroker={handleAddBroker}
          t={t}
          lang={lang}
        />
      )}

      {editUnit && (
        <UnitEditModal
          unit={editUnit}
          team={team}
          onClose={() => setEditUnit(null)}
          onSave={handleSaveUnit}
          t={t}
          lang={lang}
        />
      )}

      {editingDeal && (
        <DealEditModal
          deal={editingDeal}
          unit={editingDeal._unit}
          brokers={brokers}
          team={team}
          onClose={() => setEditingDeal(null)}
          onSubmit={handleUpdateDeal}
          onDelete={can('deals.delete') ? handleDeleteDeal : undefined}
          t={t}
          lang={lang}
        />
      )}

      {createUnitOpen && (
        <UnitCreateModal
          brandProjects={brand.projects || []}
          existingUnits={units}
          onClose={() => setCreateUnitOpen(false)}
          onSubmit={handleCreateUnit}
          t={t}
          lang={lang}
        />
      )}
    </div>
  );
}
